import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames/bind';
import { Button, Col, Form, Input, Modal, Popover, Row, Space, message } from 'antd';
import { FiUser } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import API, { removeAuthToken } from '../../helpers/API';
import {
  PASSWORD_PATTERN,
  PASSWORD_RULE_MESSAGE,
  PASSWORD_SPECIALS,
} from '../../constants/passwordPolicy';
import { getUserRole } from '../../utils/CodeMapper';
import styles from './TopHeader.module.scss';

const cx = classNames.bind(styles);
const IMS_MANAGED_PASSWORD_ROLES = ['SYSTEM', 'ADMIN', 'MANAGER'];

const UserMenu = ({ currentUser, onEditProfile, onLogout }) => (
  <Space direction="vertical" size={12}>
    <p className={cx('menuRole')}>
      {(currentUser?.customer?.name || 'BTE')} {getUserRole(currentUser?.role)}
    </p>
    <Space>
      <Button onClick={onEditProfile}>내 정보 변경</Button>
      <Button onClick={onLogout}>로그아웃</Button>
    </Space>
  </Space>
);

const TopHeader = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState();
  const [profileOpen, setProfileOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [profileForm] = Form.useForm();
  const watchedValues = Form.useWatch([], profileForm);

  useEffect(() => {
    API.get('/me').then(({ success, data }) => {
      if (success) {
        setCurrentUser(data);
      }
    });
  }, []);

  useEffect(() => {
    if (!profileOpen || !currentUser) return;

    profileForm.setFieldsValue({
      id: currentUser.id,
      role: getUserRole(currentUser.role),
      company: currentUser.customer?.name || 'BTE',
      name: currentUser.name,
      email: currentUser.email,
      phone: currentUser.phone,
      memo: currentUser.memo,
      password: undefined,
      checkPassword: undefined,
    });
  }, [currentUser, profileForm, profileOpen]);

  const summaryText = useMemo(() => {
    const name = currentUser?.name ?? '-';
    const role = currentUser?.role ?? localStorage.getItem('@h2tms/role') ?? '-';
    return `${name}님 (${role})`;
  }, [currentUser]);

  const allowPasswordChange = useMemo(
    () => !IMS_MANAGED_PASSWORD_ROLES.includes(currentUser?.role ?? ''),
    [currentUser],
  );

  const doLogout = () => {
    API.post('/logout').finally(() => {
      removeAuthToken();
      navigate('/login');
    });
  };

  const doSaveProfile = async (values) => {
    const payload = {
      name: values.name?.trim(),
      email: values.email?.trim() || undefined,
      phone: values.phone?.trim() || undefined,
      memo: values.memo ?? '',
    };

    if (values.password) {
      payload.password = values.password;
    }

    setIsSaving(true);
    const response = await API.patch('/me', payload);
    setIsSaving(false);

    if (!response?.success) {
      message.error('내 정보 변경에 실패했습니다.');
      return;
    }

    setCurrentUser(response.data);
    message.success('내 정보가 변경되었습니다.');
    setProfileOpen(false);
  };

  return (
    <>
      <header className={cx('header')}>
        <Row align="middle" justify="end" wrap={false}>
          <p className={cx('userDesc')}>{summaryText}</p>
          <Col>
            <Popover
              trigger="hover"
              content={
                <UserMenu
                  currentUser={currentUser}
                  onEditProfile={() => setProfileOpen(true)}
                  onLogout={doLogout}
                />
              }
              title={`${currentUser?.name ?? '-'}`}>
              <button type="button" aria-label="유저 정보 확인하기" className={cx('btn')}>
                <FiUser size={16} color="#444" />
              </button>
            </Popover>
          </Col>
        </Row>
      </header>

      <Modal
        width={640}
        centered
        title="내 정보 변경"
        footer={null}
        maskClosable={false}
        onCancel={() => setProfileOpen(false)}
        open={profileOpen}>
        <Form form={profileForm} layout="vertical" onFinish={doSaveProfile}>
          <Row justify="space-between" align="top" gutter={[24, 24]}>
            <Col flex="50%">
              <Form.Item label="소속업체" name="company">
                <Input disabled />
              </Form.Item>
              <Form.Item label="역할" name="role">
                <Input disabled />
              </Form.Item>
              <Form.Item
                label="이메일"
                name="email"
                rules={[{ type: 'email', message: '이메일 형식에 맞지 않습니다.' }]}>
                <Input type="email" />
              </Form.Item>
              {allowPasswordChange && (
                <>
                  <Form.Item
                    label="비밀번호"
                    name="password"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (!value) return Promise.resolve();
                          return PASSWORD_PATTERN.test(value)
                            ? Promise.resolve()
                            : Promise.reject(new Error(PASSWORD_RULE_MESSAGE));
                        },
                      },
                    ]}>
                    <Input.Password maxLength={20} />
                  </Form.Item>
                  <div className={cx('passwordHint')}>
                    허용 특수문자: {PASSWORD_SPECIALS}
                  </div>
                </>
              )}
            </Col>
            <Col flex="50%">
              <Form.Item label="아이디" name="id">
                <Input disabled />
              </Form.Item>
              <Form.Item
                label="이름"
                name="name"
                rules={[{ required: true, message: '이름을 입력해주세요.' }]}>
                <Input />
              </Form.Item>
              <Form.Item label="전화번호" name="phone">
                <Input />
              </Form.Item>
              {allowPasswordChange && (
                <Form.Item
                  label="비밀번호 확인"
                  name="checkPassword"
                  dependencies={['password']}
                  rules={[
                    {
                      validator: (_, value) => {
                        if (!watchedValues?.password && !value) return Promise.resolve();
                        if (!watchedValues?.password && value) {
                          return Promise.reject(new Error('비밀번호를 먼저 입력해주세요.'));
                        }
                        if (!value) {
                          return Promise.reject(new Error('비밀번호 확인을 해주세요.'));
                        }
                        return watchedValues?.password === value
                          ? Promise.resolve()
                          : Promise.reject(new Error('입력된 비밀번호와 다릅니다.'));
                      },
                    },
                  ]}>
                  <Input.Password maxLength={20} />
                </Form.Item>
              )}
            </Col>
          </Row>

          <Form.Item name="memo" label="메모">
            <Input.TextArea style={{ minHeight: 150 }} />
          </Form.Item>

          <Button htmlType="submit" type="primary" loading={isSaving} disabled={isSaving}>
            변경하기
          </Button>
        </Form>
      </Modal>
    </>
  );
};

export default TopHeader;
